import { useCallback } from 'react';
import { useForm, useFormState, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { datadogRum } from '@datadog/browser-rum';

import { useAnalytics } from 'src/AnalyticsContext';
import { CareTaskRequestData } from 'src/components/drawers/ResolveTaskDrawer';
import { ResolutionTypes } from 'src/constants/ResolutionTypes';
import useShiftInstances from 'src/hooks/useShiftInstances';
import { selectFacilityId, selectStaffMemberId } from 'src/store/configSlice';
import {
    CareOutcome,
    CareProvided as CareTaskCategory,
    CreateUnplannedCareTask,
    OutcomeType,
} from 'src/store/sageAdminApi';
import { buildUpdatePlannedCareTaskRequest, ChartType } from 'src/utils/residentCareFormUtils';
import { snackActions } from 'src/utils/snackBarTools';

export type UnplannedCareFormItem = {
    careTaskCategory: CareTaskCategory;
    note?: string;
};

export type UnplannedCareRecord = Partial<Record<CareTaskCategory, UnplannedCareFormItem>>;

export type PlannedCareFormValues = {
    careTaskRid: string;
    careTaskCategory: CareTaskCategory;
    // chartType is used to determine which fields to show on the form. Outcome is the charted result of the task
    chartType: ChartType;
    outcome?: OutcomeType;
    startTime?: string;
    endTime?: string;
    durationInMinutes?: number;
    note?: string;
    chartedCustomOutcomeId?: string;
    chartedReasonId?: string;
};
export type ResidentCareFormRequestData = {
    careOutcomes: CareOutcome[];
    careTaskData: CareTaskRequestData;
};

export type ResidentCareFormValues = {
    unplannedCare: UnplannedCareRecord;
    plannedCare: Record<string, PlannedCareFormValues>;
    isTestAlert: boolean;
};

const useResidentCareTaskForm = (
    residentId: string,
    alertRid?: string,
    defaultValues?: Partial<ResidentCareFormValues>
) => {
    const { trackEvent } = useAnalytics();
    const staffRid = useSelector(selectStaffMemberId);
    const facilityId = useSelector(selectFacilityId);
    const { currentTimeShift, defaultShift, isLoadingShifts } = useShiftInstances();
    const chartingShiftId = currentTimeShift?.id ?? defaultShift?.id;

    const form = useForm<ResidentCareFormValues>({
        defaultValues: {
            unplannedCare: {},
            plannedCare: {},
            isTestAlert: false,
            ...defaultValues,
        },
    });

    const { dirtyFields, errors } = useFormState({ control: form.control });

    const unplannedCare = useWatch({
        control: form.control,
        name: 'unplannedCare',
        defaultValue: {},
    });

    const isTestAlert = useWatch({
        control: form.control,
        name: 'isTestAlert',
        defaultValue: false,
    });

    // PlannedCare isn't watched directly for performance reasons
    // See: https://github.com/hlosg/sage-app/pull/1867/changes
    const plannedCareCount = Object.keys(dirtyFields.plannedCare ?? {}).length;
    const unplannedCareCount = Object.values(unplannedCare).filter(
        (item) => item?.careTaskCategory
    ).length;
    // If you select test alert, you only are going to submit that test alert outcome
    const selectedTaskCount = isTestAlert ? 1 : plannedCareCount + unplannedCareCount;

    const buildCreateUnplannedCareTask = useCallback(
        ({ careTaskCategory, note }: UnplannedCareFormItem): CreateUnplannedCareTask => {
            return {
                facilityId,
                shiftInstanceId: chartingShiftId ?? '',
                residentId,
                careTaskCategory,
                responderType: 'COMMUNITY',
                chartedOutcome: 'COMPLETED',
                chartedAtMs: Date.now(),
                ...(note ? { note } : {}),
                respondingStaffMemberIds: [staffRid],
            };
        },
        [chartingShiftId, facilityId, residentId, staffRid]
    );

    const buildTestAlertFormRequestData = useCallback((): ResidentCareFormRequestData => {
        const careOutcomes: CareOutcome[] = [
            {
                careProvided: ResolutionTypes.FoundDevice.id,
                outcome: 'COMPLETED',
                alertRid,
            },
        ];
        const careTaskData = {
            unplannedCareTask: [],
            plannedCareTasksResidentActivity: [],
            plannedCareTasksResolveTask: [],
        } as CareTaskRequestData;
        return { careOutcomes, careTaskData };
    }, [alertRid]);

    const buildFormRequestData = useCallback((): ResidentCareFormRequestData => {
        if (isTestAlert) {
            return buildTestAlertFormRequestData();
        }

        const { unplannedCare = {}, plannedCare = {} } = form.getValues();
        const plannedCareValues = Object.values(plannedCare);
        // Filter out phantom note-only entries that can survive a reset (see unplannedCareCount).
        const unplannedCareItems = (Object.values(unplannedCare) as UnplannedCareFormItem[]).filter(
            (item) => item?.careTaskCategory
        );

        const unplannedCareData = unplannedCareItems.map(buildCreateUnplannedCareTask);
        const unplannedCareOutcomes: CareOutcome[] = unplannedCareItems.map(
            ({ careTaskCategory }) => ({
                careProvided: careTaskCategory,
                outcome: 'COMPLETED',
                alertRid,
            })
        );

        const plannedCareData = plannedCareValues.map((plannedCareForm) =>
            buildUpdatePlannedCareTaskRequest(plannedCareForm, staffRid)
        );
        const plannedCareOutcomes: CareOutcome[] = plannedCareValues.map((plannedCareForm) => ({
            careProvided: plannedCareForm.careTaskCategory,
            outcome: plannedCareForm.outcome || 'COMPLETED',
            alertRid,
        }));

        const careOutcomesData = [...unplannedCareOutcomes, ...plannedCareOutcomes];

        const careTaskData = {
            unplannedCareTask: unplannedCareData,
            plannedCareTasksResidentActivity: plannedCareData.map(
                (data) => data.residentActivityRequest
            ),
            plannedCareTasksResolveTask: plannedCareData.map((data) => data.resolveTaskRequest),
        } as CareTaskRequestData;

        return { careOutcomes: careOutcomesData, careTaskData: careTaskData };
    }, [
        buildCreateUnplannedCareTask,
        buildTestAlertFormRequestData,
        staffRid,
        form,
        alertRid,
        isTestAlert,
    ]);

    // React hook form provides isValid and isDirty, but these have limitations with dynamic fields
    // https://github.com/react-hook-form/react-hook-form/issues/4740
    const formHasErrors = Object.keys(errors).length > 0;
    // RHF never prunes keys from dirtyFields once a value reverts to its default (e.g. deselecting
    // the last unplanned care task), so we derive dirtiness from the live selection count instead.
    const formIsDirty = selectedTaskCount > 0;

    const handleSubmit: typeof form.handleSubmit = (onValid, onInvalid) => {
        return form.handleSubmit(
            (data, e) => {
                // A blank shift id is rejected by the backend as invalid JSON
                if (!isTestAlert && unplannedCareCount > 0 && !chartingShiftId) {
                    datadogRum.addError(
                        new Error('No shift instance to chart unplanned care against'),
                        { facilityId, residentId, alertRid }
                    );
                    snackActions.error('Submission error. Please refresh the app and try again.');
                    return;
                }

                trackEvent('Care Form Submitted', {
                    plannedCareCount,
                    unplannedCareCount,
                });
                return onValid(data, e);
            },
            (errors, e) => {
                trackEvent('Care Form Submission Error', {
                    errorFields: Object.keys(errors),
                });
                return onInvalid?.(errors, e);
            }
        );
    };

    return {
        ...form,
        handleSubmit,
        formHasErrors,
        formIsDirty,
        isTestAlert,
        selectedTaskCount,
        plannedCareCount,
        unplannedCareCount,
        isLoadingShifts,
        buildFormRequestData,
    };
};

export default useResidentCareTaskForm;
